import { useState, useRef, useEffect, useCallback } from 'react';
import { useEmployees } from '@/hooks/useEmployees';
import styles from './EmployeeSwitcher.module.css';

interface EmployeeSwitcherProps {
  currentEmployeeId: string;
  /** Called with the new employee ID when the user picks a different employee. */
  onSwitch: (employeeId: string) => void;
}

/** Extract initials from a full name (e.g. "Sarah Chen" → "SC"). */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function EmployeeSwitcher({ currentEmployeeId, onSwitch }: EmployeeSwitcherProps) {
  const { data: employees } = useEmployees();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const currentEmployee = employees.find((e) => e.id === currentEmployeeId);
  const currentName = currentEmployee?.name ?? 'Select member';

  // Filter employees by search query
  const filtered = query.trim()
    ? employees.filter((e) => e.name.toLowerCase().includes(query.toLowerCase()))
    : [...employees];

  // Clamp highlight index when filtered list shrinks
  const clampedHighlight = filtered.length > 0 ? Math.min(highlightIndex, filtered.length - 1) : 0;

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Focus the search input when popover opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSelect = useCallback(
    (employeeId: string) => {
      setIsOpen(false);
      setQuery('');
      if (employeeId !== currentEmployeeId) {
        onSwitch(employeeId);
      }
    },
    [currentEmployeeId, onSwitch],
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-switcher-item]');
    const target = items[clampedHighlight] as HTMLElement | undefined;
    target?.scrollIntoView({ block: 'nearest' });
  }, [clampedHighlight, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[clampedHighlight]) {
          handleSelect(filtered[clampedHighlight].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setQuery('');
        break;
    }
  };

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
    if (isOpen) {
      setQuery('');
    } else {
      setHighlightIndex(0);
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={toggleOpen}
        aria-label={`Switch employee, currently ${currentName}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {/* Person icon */}
        <svg
          className={styles.triggerIcon}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="8" cy="5" r="2.5" />
          <path d="M3 14c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5" />
        </svg>
        <span>{currentName}</span>
        <svg
          className={`${styles.triggerChevron} ${isOpen ? styles.triggerChevronOpen : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 4.5l3 3 3-3" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.popover} role="dialog" aria-label="Switch employee">
          {/* Search input */}
          <div className={styles.searchWrap}>
            <svg
              className={styles.searchIcon}
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="7" cy="7" r="4.5" />
              <path d="M10.5 10.5L14 14" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Search team members\u2026"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Search team members"
              aria-activedescendant={
                filtered[clampedHighlight]
                  ? `switcher-item-${filtered[clampedHighlight].id}`
                  : undefined
              }
              role="combobox"
              aria-controls="switcher-listbox"
              aria-expanded
            />
          </div>

          {/* Results list */}
          <div className={styles.list} ref={listRef} role="listbox" id="switcher-listbox">
            {filtered.length === 0 && (
              <div className={styles.empty}>No members match &ldquo;{query}&rdquo;</div>
            )}
            {filtered.map((emp, i) => {
              const isActive = emp.id === currentEmployeeId;
              const isHighlighted = i === clampedHighlight;

              return (
                <div
                  key={emp.id}
                  id={`switcher-item-${emp.id}`}
                  data-switcher-item
                  role="option"
                  tabIndex={-1}
                  aria-selected={isActive}
                  className={[
                    styles.item,
                    isActive ? styles.itemActive : '',
                    isHighlighted ? styles.itemHighlighted : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleSelect(emp.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(emp.id);
                    }
                  }}
                  onMouseEnter={() => setHighlightIndex(i)}
                >
                  <span className={`${styles.avatar} ${isActive ? styles.itemActiveAvatar : ''}`}>
                    {initials(emp.name)}
                  </span>
                  <span className={styles.itemInfo}>
                    <span className={styles.itemName}>{emp.name}</span>
                  </span>
                  <span className={styles.checkCol}>
                    {isActive && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M3 8.5l3 3 7-7" />
                      </svg>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            {filtered.length === employees.length
              ? `${employees.length} members`
              : `${filtered.length} of ${employees.length} members`}
          </div>
        </div>
      )}
    </div>
  );
}
